import { hasRequiredRole, INTERNAL_ROUTE_ROLES } from '../../auth/authorization';

// Pure helpers for the /me Action Center. No data access lives here.

function trimText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function getTimeOfDayGreeting(now = new Date()) {
  const hour = now instanceof Date && !Number.isNaN(now.getTime()) ? now.getHours() : 12;

  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function getStaffDisplayName(profile = null, user = null) {
  return trimText(profile?.full_name)
    || trimText(user?.user_metadata?.full_name)
    || trimText(profile?.email)
    || trimText(user?.email)
    || '';
}

export function getStaffFirstName(profile = null, user = null) {
  const displayName = getStaffDisplayName(profile, user);
  if (!displayName) return '';

  const [firstPart] = displayName.split(/\s+/).filter(Boolean);
  if (!firstPart) return '';

  // Email-derived names should not render as "chris.morris.miller@example.com".
  return firstPart.includes('@') ? firstPart.split('@')[0] : firstPart;
}

export const ME_QUICK_ACTIONS = [
  {
    key: 'submit-aar',
    label: 'Submit AAR',
    description: 'Start a full AAR for an event you ran.',
    to: '/me/aars/new',
    allowedRoles: INTERNAL_ROUTE_ROLES.staff,
  },
  {
    key: 'upload-photos',
    label: 'Upload Photos',
    description: 'Create a photo-only record and attach event photos.',
    to: '/me/aars/new?workflow=photo_only&next=photos',
    allowedRoles: INTERNAL_ROUTE_ROLES.staff,
  },
  {
    key: 'view-my-aars',
    label: 'View My AARs',
    description: 'Your recent AAR records and photo counts.',
    to: '/me/aars',
    allowedRoles: INTERNAL_ROUTE_ROLES.staff,
  },
  {
    key: 'photo-library',
    label: 'Photo Library',
    description: 'Browse AAR photos available to your account.',
    to: '/me/aars/photos',
    allowedRoles: INTERNAL_ROUTE_ROLES.staff,
  },
  {
    key: 'create-qr',
    label: 'Create QR / Link',
    description: 'Generate a tracked campaign QR code or short link.',
    to: '/generator',
    allowedRoles: INTERNAL_ROUTE_ROLES.generator,
  },
  {
    key: 'voc-scoreboard',
    label: 'VOC Scoreboard',
    description: 'Open the full seasonal referral scoreboard.',
    to: '/admin/voc-scoreboard',
    allowedRoles: INTERNAL_ROUTE_ROLES.reporting,
  },
];

export function getPermittedQuickActions(role) {
  return ME_QUICK_ACTIONS.filter((action) => hasRequiredRole(role, action.allowedRoles));
}

function toCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function pluralize(count, singular, plural) {
  return count === 1 ? singular : plural;
}

/**
 * Builds the Action Center exception list from data that is already loaded.
 *
 * Two AAR states are deliberately excluded. Awaiting-review is not a backlog:
 * routine AARs must not be presented as work requiring attention. Photo absence
 * is not an exception either: plenty of legitimate AARs (virtual presentations
 * especially) never have photos, so it produced noise rather than a task.
 *
 * Each signal is one attention AREA. The toolbar counts these; the labels
 * inside carry the domain-specific record counts, which is why the two numbers
 * legitimately differ.
 */
export function buildAttentionSignals({
  aarMetrics = null,
  teamAarAccess = false,
  vocAttentionCount = 0,
  vocTopShortfall = null,
  liveUpdateRequestCount = 0,
  canSeeLiveUpdateRequests = false,
  liveUpdateRequestAnchor = '',
} = {}) {
  const signals = [];

  const followUpCount = toCount(teamAarAccess
    ? aarMetrics?.team_follow_up_required
    : aarMetrics?.my_follow_up_required);
  const incompleteDraftCount = toCount(teamAarAccess
    ? aarMetrics?.team_incomplete_drafts
    : aarMetrics?.my_incomplete_drafts);
  // Scope belongs in the label rather than in a sentence underneath it.
  const aarScope = teamAarAccess ? 'team ' : '';

  if (followUpCount > 0) {
    signals.push({
      key: 'aar-follow-up',
      tone: 'urgent',
      label: `${followUpCount} ${aarScope}${pluralize(followUpCount, 'AAR needs', 'AARs need')} follow-up`,
      actionLabel: 'Open AARs',
      to: '/me/aars',
    });
  }

  if (incompleteDraftCount > 0) {
    signals.push({
      key: 'aar-incomplete-draft',
      tone: 'urgent',
      label: `${incompleteDraftCount} incomplete ${aarScope}AAR ${pluralize(incompleteDraftCount, 'draft', 'drafts')}`,
      actionLabel: 'Finish drafts',
      to: '/me/aars',
    });
  }

  // Road Home LIVE is a temporary event workflow, so it appears on /me only as
  // an action when something is unreviewed. Reviewed history never surfaces
  // here.
  const liveCount = toCount(liveUpdateRequestCount);
  if (canSeeLiveUpdateRequests && liveCount > 0) {
    signals.push({
      key: 'live-update-requests',
      tone: 'urgent',
      label: `${liveCount} Road Home LIVE update ${pluralize(liveCount, 'request', 'requests')}`,
      actionLabel: 'Review',
      to: null,
      anchor: liveUpdateRequestAnchor || null,
    });
  }

  // Seasonal pacing is monitoring context, not an urgent task queue, so it stays
  // secondary even when the mathematical status says Watch or Behind. The count
  // is the meaningful-deficit count from teamAttentionModel, not the raw
  // Behind/Watch total, so fractional-volume territories do not inflate it.
  const vocCount = toCount(vocAttentionCount);
  if (vocCount > 0) {
    signals.push({
      key: 'voc-pacing',
      tone: 'monitor',
      label: `${vocCount} ${pluralize(vocCount, 'state is', 'states are')} behind pace`,
      // One concrete worst case beats a paragraph about the model.
      description: getTopShortfallSentence(vocTopShortfall),
      actionLabel: null,
      to: null,
    });
  }

  return signals;
}

/**
 * "North Carolina is 12 referrals short." -- the single most useful piece of
 * context for a pacing signal, or nothing when no shortfall was supplied.
 */
export function getTopShortfallSentence(topShortfall = null) {
  const state = trimText(topShortfall?.state);
  const gap = Math.round(Number(topShortfall?.gap));
  if (!state || !Number.isFinite(gap) || gap < 1) return '';

  return `${state} is ${gap} ${pluralize(gap, 'referral', 'referrals')} short.`;
}

export function hasUrgentAttention(signals = []) {
  return (Array.isArray(signals) ? signals : []).some((signal) => signal?.tone === 'urgent');
}
